import * as React from "react";
import { Checkbox as C, Select as S, Switch as Sw, Tabs as T, ToggleGroup as TG, Tooltip as Tt } from "radix-ui";
import { Check, ChevronDown, Minus } from "lucide-react";
import { cn } from "@/lib/utils";
import { Kbd } from "./input";

// ---------- Tooltip ----------
export const TooltipProvider = Tt.Provider;

export function Tooltip({
  content,
  shortcut,
  children,
  side = "top",
}: {
  content: React.ReactNode;
  shortcut?: string;
  children: React.ReactNode;
  side?: "top" | "bottom" | "left" | "right";
}) {
  return (
    <Tt.Root>
      <Tt.Trigger asChild>{children}</Tt.Trigger>
      <Tt.Portal>
        <Tt.Content
          side={side}
          sideOffset={6}
          className="z-[60] flex items-center gap-2 rounded-md bg-[#252422] px-2 py-1 text-xs font-medium text-[#f7f5f2] shadow-pop data-[state=delayed-open]:animate-fade-in dark:bg-[#3a3834]"
        >
          {content}
          {shortcut && <span className="text-[#b7b2aa]">{shortcut}</span>}
        </Tt.Content>
      </Tt.Portal>
    </Tt.Root>
  );
}

// ---------- Checkbox (square; settings & filters) ----------
export const Checkbox = React.forwardRef<React.ComponentRef<typeof C.Root>, React.ComponentPropsWithoutRef<typeof C.Root>>(
  function Checkbox({ className, ...props }, ref) {
    return (
      <C.Root
        ref={ref}
        className={cn(
          "peer inline-flex size-4 shrink-0 items-center justify-center rounded-[4px] border border-input bg-card transition-colors data-[state=checked]:border-primary data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground",
          className,
        )}
        {...props}
      >
        <C.Indicator>
          {props.checked === "indeterminate" ? <Minus className="size-3" strokeWidth={3} /> : <Check className="size-3" strokeWidth={3} />}
        </C.Indicator>
      </C.Root>
    );
  },
);

// ---------- Switch ----------
// shadcn radix-luma switch; data-checked/unchecked variants mapped to Radix's data-state.
export function Switch({
  className,
  size = "default",
  ...props
}: React.ComponentProps<typeof Sw.Root> & { size?: "sm" | "default" }) {
  return (
    <Sw.Root
      data-slot="switch"
      data-size={size}
      className={cn(
        "peer group/switch relative inline-flex shrink-0 items-center rounded-full border-2 transition-all outline-none after:absolute after:-inset-x-3 after:-inset-y-2 focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/30 aria-invalid:border-destructive aria-invalid:ring-3 aria-invalid:ring-destructive/20 data-[size=default]:h-5 data-[size=default]:w-11 data-[size=sm]:h-4 data-[size=sm]:w-7 dark:aria-invalid:border-destructive/50 dark:aria-invalid:ring-destructive/40 data-[state=checked]:border-primary data-[state=checked]:bg-primary data-[state=unchecked]:border-transparent data-[state=unchecked]:bg-input/90 data-disabled:cursor-not-allowed data-disabled:opacity-50",
        className,
      )}
      {...props}
    >
      <Sw.Thumb
        data-slot="switch-thumb"
        className="pointer-events-none block rounded-full bg-background shadow-sm ring-0 transition-transform not-dark:bg-clip-padding group-data-[size=default]/switch:h-4 group-data-[size=default]/switch:w-6 group-data-[size=sm]/switch:h-3 group-data-[size=sm]/switch:w-4 data-[state=checked]:translate-x-[calc(100%-8px)] data-[state=unchecked]:translate-x-0 dark:data-[state=checked]:bg-primary-foreground dark:data-[state=unchecked]:bg-foreground"
      />
    </Sw.Root>
  );
}

// ---------- Select ----------
export function Select({
  value,
  onValueChange,
  options,
  className,
  ariaLabel,
  placeholder,
}: {
  value: string;
  onValueChange: (v: string) => void;
  options: { value: string; label: string }[];
  className?: string;
  ariaLabel: string;
  placeholder?: string;
}) {
  return (
    <S.Root value={value} onValueChange={onValueChange}>
      <S.Trigger
        aria-label={ariaLabel}
        className={cn(
          "inline-flex h-8 items-center justify-between gap-2 rounded-md border border-input bg-card px-2.5 text-[13.5px] outline-none hover:bg-hover focus-visible:outline-2 focus-visible:outline-ring",
          className,
        )}
      >
        <S.Value placeholder={placeholder} />
        <S.Icon>
          <ChevronDown className="size-3.5 text-muted-foreground" />
        </S.Icon>
      </S.Trigger>
      <S.Portal>
        <S.Content
          position="popper"
          sideOffset={6}
          className="z-50 max-h-[320px] min-w-[var(--radix-select-trigger-width)] overflow-hidden rounded-lg border border-border bg-popover p-1 shadow-pop data-[state=open]:animate-pop-in data-[state=closed]:animate-pop-out"
        >
          <S.Viewport>
            {options.map((o) => (
              <S.Item
                key={o.value}
                value={o.value}
                className="relative flex h-8 cursor-default select-none items-center rounded-md pl-2 pr-8 text-[13.5px] outline-none data-[highlighted]:bg-hover"
              >
                <S.ItemText>{o.label}</S.ItemText>
                <S.ItemIndicator className="absolute right-2">
                  <Check className="size-4" />
                </S.ItemIndicator>
              </S.Item>
            ))}
          </S.Viewport>
        </S.Content>
      </S.Portal>
    </S.Root>
  );
}

// ---------- Segmented control ----------
export function Segmented<T extends string>({
  value,
  onChange,
  options,
  ariaLabel,
  className,
  size = "md",
}: {
  value: T;
  onChange: (v: T) => void;
  options: { value: T; label: React.ReactNode; title?: string; shortcut?: string }[];
  ariaLabel: string;
  className?: string;
  size?: "sm" | "md";
}) {
  return (
    <TG.Root
      type="single"
      value={value}
      onValueChange={(v) => v && onChange(v as T)}
      aria-label={ariaLabel}
      className={cn("inline-flex items-center gap-0.5 rounded-lg bg-muted p-0.5", className)}
    >
      {options.map((o) => (
        <TG.Item
          key={o.value}
          value={o.value}
          title={o.title}
          aria-label={o.title}
          className={cn(
            "inline-flex items-center justify-center gap-1.5 rounded-md px-2.5 font-medium text-muted-foreground transition-colors hover:text-foreground data-[state=on]:bg-card data-[state=on]:text-foreground data-[state=on]:shadow-[0_1px_2px_rgba(37,36,34,0.08),0_0_0_0.5px_rgba(37,36,34,0.08)] dark:data-[state=on]:bg-accent [&_svg]:size-3.5",
            size === "sm" ? "h-6 text-xs" : "h-7 text-[13px]",
          )}
        >
          {o.label}
          {o.shortcut && <Kbd className="h-4 min-w-4 text-[10px]">{o.shortcut}</Kbd>}
        </TG.Item>
      ))}
    </TG.Root>
  );
}

// ---------- Tabs (underline style) ----------
export const Tabs = T.Root;
export function TabsList({ className, ...props }: React.ComponentPropsWithoutRef<typeof T.List>) {
  return <T.List className={cn("flex items-center gap-4 border-b border-border", className)} {...props} />;
}
export function TabsTrigger({ className, ...props }: React.ComponentPropsWithoutRef<typeof T.Trigger>) {
  return (
    <T.Trigger
      className={cn(
        "-mb-px inline-flex h-9 items-center gap-1.5 border-b-2 border-transparent text-[13.5px] font-medium text-muted-foreground transition-colors hover:text-foreground data-[state=active]:border-foreground data-[state=active]:text-foreground",
        className,
      )}
      {...props}
    />
  );
}
export const TabsContent = T.Content;

// ---------- Badge ----------
export function Badge({
  className,
  tone = "neutral",
  children,
}: {
  className?: string;
  tone?: "neutral" | "urgent" | "outline";
  children: React.ReactNode;
}) {
  return (
    <span
      className={cn(
        "inline-flex h-5 items-center gap-1 rounded-[5px] px-1.5 text-[11.5px] font-medium tabular [&_svg]:size-3",
        tone === "neutral" && "bg-muted text-muted-foreground",
        tone === "urgent" && "bg-urgent-soft text-urgent",
        tone === "outline" && "border border-border text-muted-foreground",
        className,
      )}
    >
      {children}
    </span>
  );
}
